import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

// Design guide (light / dark / high contrast): Fluent tokens only, Lato 14px (fontSizeBase300),
// radius 4px (borderRadiusMedium), visible focus, state never conveyed by color alone,
// grid headers uppercase/small, numbers right aligned and tabular, row actions on the right.
const focusRing = {
  outlineStyle: 'solid',
  outlineWidth: tokens.strokeWidthThick,
  outlineColor: tokens.colorStrokeFocus2,
  outlineOffset: '2px',
};

export const useFilesExplorerStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    minWidth: 0,
  },
  rootDragging: {
    outlineStyle: 'dashed',
    outlineWidth: tokens.strokeWidthThick,
    outlineColor: tokens.colorBrandStroke1,
    outlineOffset: tokens.spacingHorizontalS,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  dropHint: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: tokens.spacingHorizontalS,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.border(tokens.strokeWidthThick, 'dashed', tokens.colorBrandStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
  },
  lock: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    ':focus-visible': focusRing,
  },
  headerRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalM,
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  folderTitle: {
    ...shorthands.margin(0),
    fontSize: tokens.fontSizeBase600,
    lineHeight: tokens.lineHeightBase600,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  summary: {
    color: tokens.colorNeutralForeground2,
    fontVariantNumeric: 'tabular-nums',
  },
  breadcrumb: {
    minWidth: 0,
  },
  toolbarRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalS,
  },
  search: {
    width: '320px',
    maxWidth: '100%',
  },
  toolbar: {
    ...shorthands.padding(0),
  },
  surface: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    overflowX: 'auto',
  },
  headerCell: {
    textTransform: 'uppercase',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground2,
    letterSpacing: '0.02em',
  },
  row: {
    cursor: 'default',
    ':focus-visible': focusRing,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  nameButton: {
    minWidth: 0,
    maxWidth: '100%',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    textAlign: 'left',
    ':focus-visible': focusRing,
  },
  icon: {
    flexShrink: 0,
    fontSize: '24px',
    color: tokens.colorNeutralForeground2,
  },
  iconFolder: {
    color: tokens.colorBrandForeground1,
  },
  iconPdf: {
    color: tokens.colorPaletteRedForeground1,
  },
  iconSpreadsheet: {
    color: tokens.colorPaletteGreenForeground1,
  },
  secondaryText: {
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  numericCell: {
    justifyContent: 'flex-end',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  actionsCell: {
    justifyContent: 'flex-end',
    columnGap: tokens.spacingHorizontalXS,
  },
  // ---- Grid view (OneDrive-like tiles) ----
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(188px, 1fr))',
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalM,
    listStyleType: 'none !important',
    ...shorthands.margin(0),
    ...shorthands.padding(0),
  },
  // display:block quita el marcador aunque una regla global ponga list-style en ul > li con !important
  tileItem: {
    display: 'block',
    listStyleType: 'none !important',
    minWidth: 0,
    ...shorthands.margin(0),
    ...shorthands.padding(0),
  },
  tile: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    overflowY: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    transitionProperty: 'box-shadow, border-color',
    transitionDuration: tokens.durationFaster,
    cursor: 'default',
    ':hover': {
      boxShadow: tokens.shadow8,
      ...shorthands.borderColor(tokens.colorNeutralStroke1Hover),
    },
    ':focus-visible': focusRing,
    ':focus-within': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
    },
    '&:hover [data-tile-actions]': { opacity: 1 },
    '&:focus-within [data-tile-actions]': { opacity: 1 },
  },
  tileFolder: {},
  tilePreview: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '116px',
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
  },
  tilePreviewFolder: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  tileIcon: {
    flexShrink: 0,
    fontSize: '48px',
    color: tokens.colorNeutralForeground2,
  },
  tileBadge: {
    position: 'absolute',
    left: tokens.spacingHorizontalS,
    bottom: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    fontWeight: tokens.fontWeightBold,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    ...shorthands.padding('2px', tokens.spacingHorizontalXS),
  },
  tileActions: {
    position: 'absolute',
    top: tokens.spacingVerticalXS,
    right: tokens.spacingHorizontalXS,
    display: 'flex',
    alignItems: 'center',
    columnGap: '2px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding('2px'),
    boxShadow: tokens.shadow4,
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: tokens.durationFaster,
    '@media (hover: none)': { opacity: 1 },
    '@media (forced-colors: active)': { opacity: 1 },
  },
  tileBody: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    minWidth: 0,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM, tokens.spacingVerticalM),
  },
  tileNameRow: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  tileName: {
    flexGrow: 1,
    minWidth: 0,
    minHeight: 0,
    height: 'auto',
    justifyContent: 'flex-start',
    textAlign: 'left',
    whiteSpace: 'normal',
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    ...shorthands.padding(0),
    ':focus-visible': focusRing,
  },
  tileNameText: {
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    overflowX: 'hidden',
    overflowY: 'hidden',
    overflowWrap: 'anywhere',
    textAlign: 'left',
  },
  tileMeta: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    minWidth: 0,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground2,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalS,
    textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'dashed', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
  },
  emptyIcon: {
    fontSize: '32px',
    color: tokens.colorNeutralForeground2,
  },
  emptyTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  emptyText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
  },
  skeletonRow: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr 120px 80px',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
  },
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
  },
});
